import { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Trash01, Database01, Download01, Upload01, AlertTriangle, RefreshCw01 } from "@untitledui/icons";
import { Button } from "@/components/base/buttons/button";
import { Badge } from "@/components/base/badges/badges";
import { LoadingIndicator } from "@/components/application/loading-indicator/loading-indicator";
import { AppDialog } from "@/components/app/AppDialog";
import { formatDateTime } from "@/lib/format";
import { TOKEN_STATES } from "@/lib/tokens";
import { SectionHeader } from "@/components/application/section-headers/section-headers";
import { AppSelect } from "@/components/app/AppSelect";
import {
  useMaintenanceStats,
  useBackups,
  useLogCleanup,
  type CleanupEntity,
  useTokenCleanup,
  useCreateBackup,
  useRestoreBackup,
  useDeleteBackup,
  useFullReset,
  exportTokens,
  usePreviewImport,
  useImportTokens,
  useSchedulers,
  useSetAutoCleanup,
  useSetExpirationScheduler,
  type BackupInfo,
} from "@/lib/maintenance";

const inputCls =
  "rounded-lg bg-primary px-3 py-2 text-sm text-primary shadow-xs ring-1 ring-primary outline-none focus:ring-2 focus:ring-brand";

function Card({ title, children }: { title: React.ReactNode; children: React.ReactNode }) {
  return (
    <section className="rounded-xl bg-primary p-5 ring-1 ring-secondary">
      <h2 className="mb-3 text-sm font-semibold text-secondary">{title}</h2>
      {children}
    </section>
  );
}

export function MaintenancePage() {
  const { t } = useTranslation();
  const stats = useMaintenanceStats();

  return (
    <div className="flex flex-col gap-6">
      <SectionHeader.Root>
        <SectionHeader.Group>
          <div className="flex-1">
            <SectionHeader.Heading>{t("maintenance.title")}</SectionHeader.Heading>
            <SectionHeader.Subheading>{t("maintenance.subtitle")}</SectionHeader.Subheading>
          </div>
        </SectionHeader.Group>
      </SectionHeader.Root>

      {/* Statistiky */}
      <Card
        title={
          <span className="flex items-center justify-between gap-2">
            {t("maintenance.stats")}
            <Button size="sm" color="secondary" iconLeading={RefreshCw01} isDisabled={stats.isFetching} onPress={() => void stats.refetch()}>
              {t("maintenance.refreshStats")}
            </Button>
          </span>
        }
      >
        {stats.isLoading && <LoadingIndicator />}
        {stats.data && (
          <div className="flex flex-col gap-4">
            <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
              <Stat label={t("maintenance.httpLogs")} value={stats.data.httpLogsTotal} sub={`24h ${stats.data.httpLogsLast24h} · 7d ${stats.data.httpLogsLast7d}`} />
              <Stat label={t("maintenance.txLogs")} value={stats.data.transactionLogsTotal} sub={`24h ${stats.data.transactionLogsLast24h} · 7d ${stats.data.transactionLogsLast7d}`} />
              <Stat label={t("maintenance.tokensTotal")} value={stats.data.tokensTotal} />
              <Stat label={t("dashboard.systemStats.activationEvents")} value={stats.data.activationEventsTotal} sub={`24h ${stats.data.activationEventsLast24h} · 7d ${stats.data.activationEventsLast7d}`} />
            </div>
            {stats.data.tokensByState && Object.keys(stats.data.tokensByState).length > 0 && (
              <div className="flex flex-wrap gap-2">
                {Object.entries(stats.data.tokensByState).map(([st, n]) => (
                  <Badge key={st} type="pill-color" size="sm" color="gray">{st}: {n}</Badge>
                ))}
              </div>
            )}
          </div>
        )}
      </Card>

      {/* Schedulery */}
      <SchedulersCard />

      {/* Cleanup logů */}
      <LogCleanupCard entity="http-logs" title={t("maintenance.cleanupHttp")} />
      <LogCleanupCard entity="transaction-logs" title={t("maintenance.cleanupTx")} />
      <LogCleanupCard entity="activation-events" title={t("maintenance.cleanupActivationEvents")} />
      <TokenCleanupCard />

      {/* Zálohy */}
      <BackupsCard />

      {/* Export / Import */}
      <ExportImportCard />

      {/* Danger zone */}
      <FullResetCard />
    </div>
  );
}

function Stat({ label, value, sub }: { label: string; value: number; sub?: string }) {
  return (
    <div className="rounded-lg bg-secondary p-3 ring-1 ring-secondary">
      <div className="text-xl font-semibold text-primary">{value.toLocaleString()}</div>
      <div className="text-xs text-tertiary">{label}</div>
      {sub && <div className="mt-0.5 text-[11px] text-tertiary tabular-nums">{sub}</div>}
    </div>
  );
}

/** Schedulery: automatický úklid logů + expirace tokenů (enable + retention + last-run). */
function SchedulersCard() {
  const { t } = useTranslation();
  const status = useSchedulers();
  const setCleanup = useSetAutoCleanup();
  const setExpiration = useSetExpirationScheduler();
  const [retention, setRetention] = useState<number | null>(null);

  const ac = status.data?.autoCleanup;
  const te = status.data?.tokenExpiration;
  const retentionVal = retention ?? ac?.retentionDays ?? 30;

  return (
    <Card title={t("maintenance.schedulers")}>
      {status.isLoading && <LoadingIndicator />}
      {status.data && (
        <div className="flex flex-col gap-5">
          {/* Auto log cleanup */}
          <div className="flex flex-col gap-2 rounded-lg bg-secondary p-4 ring-1 ring-secondary">
            <div className="flex flex-wrap items-center gap-3">
              <span className="text-sm font-semibold text-primary">{t("maintenance.autoCleanupScheduler")}</span>
              <label className="flex items-center gap-2 text-sm text-secondary">
                <input
                  type="checkbox"
                  checked={!!ac?.enabled}
                  disabled={setCleanup.isPending}
                  onChange={(e) => setCleanup.mutate({ enabled: e.target.checked, retentionDays: retentionVal })}
                />
                {t("maintenance.enabled")}
              </label>
              <label className="flex items-center gap-2 text-sm text-secondary">
                {t("maintenance.retentionDays")}
                <input
                  type="number"
                  min={1}
                  className={inputCls + " w-24"}
                  value={retentionVal}
                  onChange={(e) => setRetention(Number(e.target.value))}
                />
              </label>
              <Button
                size="sm"
                color="secondary"
                isDisabled={setCleanup.isPending}
                onPress={() => setCleanup.mutate({ enabled: !!ac?.enabled, retentionDays: retentionVal })}
              >
                {t("common.save")}
              </Button>
            </div>
            {ac?.lastRunAt && (
              <p className="text-xs text-tertiary">
                {t("maintenance.lastRun")}: {formatDateTime(ac.lastRunAt)}
                {ac.lastRunResult ? ` — ${ac.lastRunResult}` : ""}
              </p>
            )}
          </div>

          {/* Token expiration */}
          <div className="flex flex-col gap-2 rounded-lg bg-secondary p-4 ring-1 ring-secondary">
            <div className="flex flex-wrap items-center gap-3">
              <span className="text-sm font-semibold text-primary">{t("maintenance.tokenExpirationScheduler")}</span>
              <label className="flex items-center gap-2 text-sm text-secondary">
                <input
                  type="checkbox"
                  checked={!!te?.enabled}
                  disabled={setExpiration.isPending}
                  onChange={(e) => setExpiration.mutate({ enabled: e.target.checked })}
                />
                {t("maintenance.enabled")}
              </label>
            </div>
            {te?.lastRunAt && (
              <p className="text-xs text-tertiary">
                {t("maintenance.lastRun")}: {formatDateTime(te.lastRunAt)}
                {te.lastRunResult ? ` — ${te.lastRunResult}` : ""}
              </p>
            )}
          </div>
        </div>
      )}
    </Card>
  );
}

function LogCleanupCard({ entity, title }: { entity: CleanupEntity; title: string }) {
  const { t } = useTranslation();
  const { preview, remove } = useLogCleanup(entity);
  const [days, setDays] = useState(30);
  const [confirm, setConfirm] = useState(false);

  return (
    <Card title={title}>
      <div className="flex flex-wrap items-end gap-3">
        <label className="flex flex-col gap-1">
          <span className="text-xs font-medium text-tertiary">{t("maintenance.olderThanDays")}</span>
          <input type="number" min={0} className={inputCls + " w-32"} value={days} onChange={(e) => setDays(Number(e.target.value))} />
        </label>
        <Button size="sm" color="secondary" isDisabled={preview.isPending} onPress={() => preview.mutate(days)}>
          {t("maintenance.preview")}
        </Button>
        <Button size="sm" color="primary-destructive" iconLeading={Trash01} onPress={() => setConfirm(true)}>
          {t("maintenance.delete")}
        </Button>
      </div>
      {preview.data && (
        <p className="mt-3 text-sm text-secondary">
          {t("maintenance.wouldDelete", { count: preview.data.recordsToDelete })}
        </p>
      )}
      {remove.data && (
        <p className="mt-2 text-sm text-success-primary">{t("maintenance.deleted", { count: remove.data.deletedRecords })}</p>
      )}
      {confirm && (
        <ConfirmDialog
          title={t("maintenance.delete")}
          description={t("maintenance.cleanupConfirm", { days })}
          pending={remove.isPending}
          onConfirm={async () => {
            await remove.mutateAsync(days);
            setConfirm(false);
          }}
          onClose={() => setConfirm(false)}
        />
      )}
    </Card>
  );
}

function TokenCleanupCard() {
  const { t } = useTranslation();
  const { preview, remove } = useTokenCleanup();
  const [states, setStates] = useState<string[]>(["DELETED"]);
  const [days, setDays] = useState<string>("");
  const [authServerType, setAuthServerType] = useState("");
  const [confirm, setConfirm] = useState(false);

  const toggle = (s: string) => setStates((cur) => (cur.includes(s) ? cur.filter((x) => x !== s) : [...cur, s]));
  const args = { states, olderThanDays: days ? Number(days) : null, authServerType: authServerType || null };
  const hasActive = states.includes("ACTIVE");

  return (
    <Card title={t("maintenance.cleanupTokens")}>
      <div className="flex flex-col gap-3">
        <div className="flex flex-wrap gap-3">
          {TOKEN_STATES.map((s) => (
            <label key={s} className="flex items-center gap-1.5 text-sm text-secondary">
              <input type="checkbox" checked={states.includes(s)} onChange={() => toggle(s)} className="size-4" />
              {s}
            </label>
          ))}
        </div>
        {hasActive && (
          <div className="rounded-lg bg-secondary px-3 py-2 text-sm text-error-primary ring-1 ring-error">
            ⚠ {t("maintenance.activeCascadeWarning")}
          </div>
        )}
        <div className="flex flex-wrap items-end gap-3">
          <label className="flex flex-col gap-1">
            <span className="text-xs font-medium text-tertiary">{t("maintenance.olderThanDaysOpt")}</span>
            <input type="number" min={0} className={inputCls + " w-32"} value={days} onChange={(e) => setDays(e.target.value)} placeholder="—" />
          </label>
          <label className="flex flex-col gap-1">
            <span className="text-xs font-medium text-tertiary">{t("tokens.col.connector")}</span>
            <AppSelect fullWidth={false} value={authServerType} onChange={(e) => setAuthServerType(e.target.value)}>
              <option value="">{t("tokens.filter.all")}</option>
              {["ECOBRA", "ECOBRA_SOAP", "CSOB_ECHANNELS", "KB_CAAS", "PRIVAT"].map((s) => <option key={s} value={s}>{s}</option>)}
            </AppSelect>
          </label>
          <Button size="sm" color="secondary" isDisabled={preview.isPending || states.length === 0} onPress={() => preview.mutate(args)}>
            {t("maintenance.preview")}
          </Button>
          <Button size="sm" color="primary-destructive" iconLeading={Trash01} isDisabled={states.length === 0} onPress={() => setConfirm(true)}>
            {t("maintenance.delete")}
          </Button>
        </div>
        {preview.data && (
          <>
            <p className="text-sm text-secondary">{t("maintenance.wouldDelete", { count: preview.data.tokensToDelete })}</p>
            {preview.data.warningMessage && <p className="text-sm text-warning-primary">{preview.data.warningMessage}</p>}
          </>
        )}
        {remove.data && (
          <p className="text-sm text-success-primary">{t("maintenance.deleted", { count: remove.data.deletedTokens })}</p>
        )}
      </div>
      {confirm && (
        <ConfirmDialog
          title={t("maintenance.cleanupTokens")}
          description={t("maintenance.tokenCleanupConfirm", { states: states.join(", ") })}
          pending={remove.isPending}
          onConfirm={async () => {
            await remove.mutateAsync(args);
            setConfirm(false);
          }}
          onClose={() => setConfirm(false)}
        />
      )}
    </Card>
  );
}

function BackupsCard() {
  const { t } = useTranslation();
  const backups = useBackups();
  const create = useCreateBackup();
  const restore = useRestoreBackup();
  const del = useDeleteBackup();
  const [restoreT, setRestoreT] = useState<BackupInfo | null>(null);
  const [deleteT, setDeleteT] = useState<BackupInfo | null>(null);

  return (
    <Card title={t("maintenance.backups")}>
      <div className="mb-3">
        <Button size="sm" color="secondary" iconLeading={Database01} isLoading={create.isPending} onPress={() => create.mutate()}>
          {t("maintenance.createBackup")}
        </Button>
      </div>
      {backups.isLoading && <LoadingIndicator />}
      {backups.data && backups.data.backups.length === 0 && <p className="text-sm text-tertiary">{t("maintenance.noBackups")}</p>}
      {backups.data && backups.data.backups.length > 0 && (
        <div className="flex flex-col gap-2">
          {backups.data.backups.map((b) => (
            <div key={b.filename} className="flex flex-wrap items-center justify-between gap-2 rounded-lg bg-secondary px-3 py-2 ring-1 ring-secondary">
              <div className="min-w-0">
                <div className="truncate font-mono text-sm text-primary">{b.filename}</div>
                <div className="text-xs text-tertiary">{b.sizeFormatted} · {formatDateTime(b.createdAt)}</div>
              </div>
              <div className="flex gap-2">
                <Button size="sm" color="secondary" onPress={() => setRestoreT(b)}>{t("maintenance.restore")}</Button>
                <Button size="sm" color="tertiary" iconLeading={Trash01} aria-label={t("maintenance.delete")} onPress={() => setDeleteT(b)} />
              </div>
            </div>
          ))}
        </div>
      )}
      {restoreT && (
        <ConfirmDialog
          title={t("maintenance.restore")}
          description={t("maintenance.restoreConfirm", { filename: restoreT.filename })}
          destructive
          pending={restore.isPending}
          onConfirm={async () => { await restore.mutateAsync(restoreT.filename); setRestoreT(null); }}
          onClose={() => setRestoreT(null)}
        />
      )}
      {deleteT && (
        <ConfirmDialog
          title={t("maintenance.delete")}
          description={t("maintenance.deleteBackupConfirm", { filename: deleteT.filename })}
          destructive
          pending={del.isPending}
          onConfirm={async () => { await del.mutateAsync(deleteT.filename); setDeleteT(null); }}
          onClose={() => setDeleteT(null)}
        />
      )}
    </Card>
  );
}

function ExportImportCard() {
  const { t } = useTranslation();
  const [states, setStates] = useState<string[]>([]);
  const [includePasswords, setIncludePasswords] = useState(false);
  const [includeActivationEvents, setIncludeActivationEvents] = useState(false);
  const [exportAuthServer, setExportAuthServer] = useState("");
  const [exporting, setExporting] = useState(false);
  const [exportErr, setExportErr] = useState<string | null>(null);
  const toggle = (s: string) => setStates((cur) => (cur.includes(s) ? cur.filter((x) => x !== s) : [...cur, s]));

  const preview = usePreviewImport();
  const importT = useImportTokens();
  const fileRef = useRef<HTMLInputElement>(null);
  const [file, setFile] = useState<File | null>(null);
  const [overwrite, setOverwrite] = useState(false);

  const doExport = async () => {
    setExportErr(null);
    setExporting(true);
    const r = await exportTokens({
      states: states.length ? states : null,
      authServerType: exportAuthServer || null,
      includePasswords,
      includeActivationEvents,
    });
    setExporting(false);
    if (!r.ok) setExportErr(r.error ?? "error");
  };

  return (
    <Card title={t("maintenance.exportImport")}>
      <div className="flex flex-col gap-5">
        {/* Export */}
        <div>
          <h3 className="mb-2 text-xs font-semibold tracking-wide text-quaternary uppercase">{t("maintenance.export")}</h3>
          <div className="flex flex-wrap gap-3">
            {TOKEN_STATES.map((s) => (
              <label key={s} className="flex items-center gap-1.5 text-sm text-secondary">
                <input type="checkbox" checked={states.includes(s)} onChange={() => toggle(s)} className="size-4" />
                {s}
              </label>
            ))}
          </div>
          <div className="mt-2 flex flex-wrap items-center gap-4">
            <label className="flex items-center gap-1.5 text-sm text-secondary">
              <input type="checkbox" checked={includePasswords} onChange={(e) => setIncludePasswords(e.target.checked)} className="size-4" />
              {t("maintenance.includePasswords")}
            </label>
            <label className="flex items-center gap-1.5 text-sm text-secondary">
              <input type="checkbox" checked={includeActivationEvents} onChange={(e) => setIncludeActivationEvents(e.target.checked)} className="size-4" />
              {t("maintenance.includeActivationEvents")}
            </label>
            <label className="flex items-center gap-1.5 text-sm text-secondary">
              {t("tokens.col.connector")}:
              <AppSelect fullWidth={false} value={exportAuthServer} onChange={(e) => setExportAuthServer(e.target.value)}>
                <option value="">{t("tokens.filter.all")}</option>
                {["ECOBRA", "ECOBRA_SOAP", "CSOB_ECHANNELS", "KB_CAAS", "PRIVAT"].map((s) => <option key={s} value={s}>{s}</option>)}
              </AppSelect>
            </label>
          </div>
          <div className="mt-3">
            <Button size="sm" color="secondary" iconLeading={Download01} isLoading={exporting} onPress={() => void doExport()}>
              {t("maintenance.exportBtn")}
            </Button>
            {exportErr && <span className="ml-3 text-sm text-error-primary">{exportErr}</span>}
          </div>
        </div>

        {/* Import */}
        <div>
          <h3 className="mb-2 text-xs font-semibold tracking-wide text-quaternary uppercase">{t("maintenance.import")}</h3>
          <div className="flex flex-wrap items-center gap-3">
            <input
              ref={fileRef}
              type="file"
              accept="application/json,.json"
              className="text-sm text-secondary"
              onChange={(e) => {
                const f = e.target.files?.[0] ?? null;
                setFile(f);
                if (f) preview.mutate(f);
              }}
            />
            <label className="flex items-center gap-1.5 text-sm text-secondary">
              <input type="checkbox" checked={overwrite} onChange={(e) => setOverwrite(e.target.checked)} className="size-4" />
              {t("maintenance.overwrite")}
            </label>
            <Button
              size="sm"
              color="secondary"
              iconLeading={Upload01}
              isDisabled={!file || importT.isPending || preview.data?.valid === false}
              onPress={() => file && importT.mutate({ file, overwriteExisting: overwrite })}
            >
              {t("maintenance.importBtn")}
            </Button>
          </div>
          {preview.data && (
            <p className="mt-2 text-sm text-secondary">
              {preview.data.valid
                ? t("maintenance.importPreview", {
                    total: preview.data.totalTokens,
                    fresh: preview.data.newTokens,
                    existing: preview.data.existingTokens,
                  })
                : (preview.data.validationErrors ?? []).join("; ")}
            </p>
          )}
          {importT.data && (
            <p className="mt-2 text-sm text-success-primary">
              {t("maintenance.imported", {
                updated: importT.data.updatedCount,
                skipped: importT.data.skippedCount,
                failed: importT.data.failedCount,
              })}
            </p>
          )}
        </div>
      </div>
    </Card>
  );
}

function FullResetCard() {
  const { t } = useTranslation();
  const reset = useFullReset();
  const [confirm, setConfirm] = useState(false);
  const [typed, setTyped] = useState("");

  return (
    <section className="rounded-xl bg-primary p-5 ring-1 ring-error">
      <div className="mb-2 flex items-center gap-2">
        <AlertTriangle className="size-5 text-error-primary" />
        <h2 className="text-sm font-semibold text-error-primary">{t("maintenance.dangerZone")}</h2>
      </div>
      <p className="mb-3 text-sm text-tertiary">{t("maintenance.fullResetDesc")}</p>
      <Button size="sm" color="primary-destructive" iconLeading={Trash01} onPress={() => { setTyped(""); setConfirm(true); }}>
        {t("maintenance.fullReset")}
      </Button>
      {confirm && (
        <AppDialog
          isOpen
          onClose={() => setConfirm(false)}
          title={t("maintenance.fullReset")}
          description={t("maintenance.fullResetConfirm")}
          footer={
            <>
              <Button color="secondary" onPress={() => setConfirm(false)} isDisabled={reset.isPending}>{t("common.cancel")}</Button>
              <Button
                color="primary-destructive"
                isDisabled={typed !== "RESET" || reset.isPending}
                onPress={async () => { await reset.mutateAsync(); setConfirm(false); }}
              >
                {t("maintenance.fullReset")}
              </Button>
            </>
          }
        >
          <label className="flex flex-col gap-1.5">
            <span className="text-sm text-secondary">{t("maintenance.typeReset")}</span>
            <input className={inputCls + " w-full"} value={typed} onChange={(e) => setTyped(e.target.value)} placeholder="RESET" />
          </label>
        </AppDialog>
      )}
    </section>
  );
}

function ConfirmDialog({
  title,
  description,
  pending,
  destructive,
  onConfirm,
  onClose,
}: {
  title: string;
  description: string;
  pending: boolean;
  destructive?: boolean;
  onConfirm: () => void | Promise<void>;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  return (
    <AppDialog
      isOpen
      onClose={onClose}
      title={title}
      description={description}
      footer={
        <>
          <Button color="secondary" onPress={onClose} isDisabled={pending}>{t("common.cancel")}</Button>
          <Button color={destructive ? "primary-destructive" : "primary"} onPress={() => void onConfirm()} isDisabled={pending}>
            {t("common.confirm")}
          </Button>
        </>
      }
    />
  );
}
